<template>
  <div class="package-list">
    <div class="page-header">
      <van-icon name="arrow-left" size="24" class="back-icon" @click="goBack" />
      <h2 class="page-title">发版记录管理</h2>
    </div>
    
    <!-- 类型页签 -->
    <div class="tab-section">
      <van-tabs v-model="activeTab" @change="onTabChange" class="type-tabs">
        <van-tab title="全部" name=""></van-tab>
        <van-tab v-for="item in packageTypes" :key="item.type" :title="item.name" :name="item.type"></van-tab>
      </van-tabs>
    </div>
    
    <!-- 发版记录列表 -->
    <div class="list-section">
      <van-empty v-if="currentTabData.length === 0" description="暂无发版记录" />
      <van-list
        v-else
        v-model="tabLoading[activeTab]"
        :finished="tabFinished[activeTab]"
        finished-text="没有更多记录了"
        @load="onLoad"
        class="release-list"
      >
        <van-cell-group inset class="record-item" v-for="(item, index) in currentTabData" :key="index">
          <div class="record-header">
            <div class="record-title">
              <span class="package-type" :style="{ backgroundColor: getTypeColor(item.type) }">{{ getTypeName(item.type) }}</span>
              <span class="version-number">v{{ item.version }}</span>
            </div>
            <van-tag :type="item.is_current ? 'success' : 'default'" class="current-tag">
              {{ item.is_current ? '当前版本' : '历史版本' }}
            </van-tag>
          </div>
          <div class="record-content">
            <div class="record-info">
              <div class="info-item">
                <span class="label">上传时间：</span>
                <span class="value">{{ item.upload_time }}</span>
              </div>
              <div class="info-item">
                <span class="label">上传人：</span>
                <span class="value">{{ item.uploader || '未知' }}</span>
              </div>
              <div class="info-item" v-if="item.file_size_formatted">
                <span class="label">文件大小：</span>
                <span class="value">{{ item.file_size_formatted }}</span>
              </div>
              <div class="info-item" v-if="item.download_count !== undefined">
                <span class="label">下载次数：</span>
                <span class="value">{{ item.download_count }} 次</span>
              </div>
            </div>
            <div class="record-desc" v-if="item.description">
              <div class="label">更新说明：</div>
              <div class="desc-content">{{ item.description }}</div>
            </div>
          </div>
          <div class="record-actions">
            <van-button type="primary" size="small" @click="downloadPackage(item)" :loading="downloadingId === item.id">
              下载
            </van-button>
          </div>
        </van-cell-group>
      </van-list>
    </div>
  </div>
</template>

<script>
import { getPackageTypes, downloadPackage, getPackageList } from '@/api/package'
export default {
  name: 'PackageList',
  data() {
    return {
      activeTab: '',
      packageTypes: [],
      tabData: {}, // 存储每个页签的数据 { tabName: [data] }
      tabLoading: { '': false }, // 存储每个页签的加载状态
      tabFinished: { '': false }, // 存储每个页签的完成状态
      tabPage: { '': 1 }, // 存储每个页签的当前页码
      pageSize: 20,
      downloadingId: null
    }
  },
  computed: {
    // 获取当前页签的数据
    currentTabData() {
      return this.tabData[this.activeTab] || []
    }
  },
  created() {
    this.fetchTypes()
  },
  mounted() {
    // 初始加载全部数据
    this.fetchPackageList()
  },
  methods: {
    async fetchTypes() {
      try {
        const res = await getPackageTypes()
        console.log('获取包类型响应:', res)
        const list = Array.isArray(res && res.data) ? res.data : []
        const iconMap = {
          pos_android: 'apps-o',
          kiosk: 'desktop-o',
          // Windows 使用 desktop 图标，更贴近 PC 端
          pos_windows: 'desktop-o',
          kds: 'tv-o',
          // 后端 value 为 'pos'，前端映射到安卓图标
          pos: 'apps-o'
        }

        this.packageTypes = list.map(item => {
          const type = item.type || item.value || item.key
          // 初始化每个页签的状态
          this.$set(this.tabData, type, [])
          this.$set(this.tabLoading, type, false)
          this.$set(this.tabFinished, type, false)
          this.$set(this.tabPage, type, 1)
          return {
            type,
            name: item.name,
            description: item.description,
            icon: (item.icon && String(item.icon)) || iconMap[type] || 'apps-o'
          }
        })
      } catch (e) {
        console.error('获取包类型失败:', e)
        this.$toast('获取包类型失败')
      }
    },
    
    async fetchPackageList() {
      const tab = this.activeTab
      if (this.tabLoading[tab]) return
      
      this.$set(this.tabLoading, tab, true)
      try {
        // 调用真实接口获取发版记录
        const res = await getPackageList({
          page: this.tabPage[tab],
          limit: this.pageSize,
          type: tab || undefined
        })
        
        if (res.code === 200 && res.data) {
          const { list, total } = res.data
          
          // 格式化数据，适配前端展示
          const formattedList = list.map(item => ({
            id: item.id,
            type: item.package_type,
            version: item.version,
            upload_time: item.upload_time_formatted || new Date(item.upload_time * 1000).toLocaleString('zh-CN'),
            uploader: item.uploader || '未知',
            file_size: item.file_size,
            file_size_formatted: item.file_size_formatted,
            description: item.description,
            is_current: item.is_active === 1,
            download_count: item.download_count,
            download_url: item.download_url
          }))
          
          // 根据当前页签存储数据
          if (this.tabPage[tab] === 1) {
            this.$set(this.tabData, tab, formattedList)
          } else {
            this.$set(this.tabData, tab, [...this.tabData[tab], ...formattedList])
          }
          
          // 更新完成状态
          this.$set(this.tabFinished, tab, this.tabData[tab].length >= total)
          // 更新页码
          this.$set(this.tabPage, tab, this.tabPage[tab] + 1)
        } else {
          throw new Error(res.message || '获取发版记录失败')
        }
      } catch (e) {
        console.error('获取发版记录失败:', e)
        this.$toast(e.message || '获取发版记录失败')
      } finally {
        this.$set(this.tabLoading, this.activeTab, false)
      }
    },
    
    onLoad() {
      // 根据当前激活页签加载数据
      this.fetchPackageList()
    },
    
    onTabChange(tab) {
      this.activeTab = tab
      // 如果当前页签没有数据且未完成加载，则加载数据
      if ((!this.tabData[tab] || this.tabData[tab].length === 0) && !this.tabFinished[tab]) {
        this.fetchPackageList()
      }
    },
    
    getTypeName(type) {
      const item = this.packageTypes.find(t => t.type === type)
      return item ? item.name : type
    },
    
    getTypeColor(type) {
      const colorMap = {
        pos_android: '#007AFF',
        pos_windows: '#5AC8FA',
        kiosk: '#4CD964',
        kds: '#FF9500'
      }
      return colorMap[type] || '#8E8E93'
    },
    
    async downloadPackage(item) {
      try {
        this.downloadingId = item.id
        
        if (item.download_url) {
          // 使用直接下载方式
          window.open(item.download_url, '_blank')
          this.$toast('开始下载')
        } else {
          // 兼容旧接口
          await downloadPackage(item.type)
          this.$toast('下载成功')
        }
      } catch (e) {
        console.error('下载失败:', e)
        this.$toast('下载失败')
      } finally {
        this.downloadingId = null
      }
    },
    
    goBack() {
      this.$router.push('/home')
    }
  }
}
</script>

<style lang="scss" scoped>
.package-list {
  padding: 20px;
  background-color: #f5f7fa;
  min-height: 100vh;
}

.page-header {
  display: flex;
  align-items: center;
  margin-bottom: 30px;

  .back-icon {
    color: #2c3e50;
    cursor: pointer;
    padding: 8px;
    margin-right: 16px;
    border-radius: 8px;
    transition: all 0.3s ease;

    &:hover {
      background-color: rgba(25, 137, 250, 0.1);
      color: #1989fa;
    }
  }
}

.page-title {
  margin: 0;
  font-size: 24px;
  font-weight: 600;
  color: #2c3e50;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 0;
    width: 40px;
    height: 3px;
    background: #1989fa;
    border-radius: 2px;
  }
}

.tab-section {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
  margin-bottom: 20px;
  overflow: hidden;
  
  .type-tabs {
    :deep(.van-tabs__nav) {
      background: #fafafa;
      border-bottom: 1px solid #f0f0f0;
    }
    
    :deep(.van-tab) {
      font-size: 14px;
      font-weight: 500;
    }
    
    :deep(.van-tabs__line) {
      background-color: #1989fa;
      height: 3px;
    }
    
    :deep(.van-tab--active) {
      color: #1989fa;
      font-weight: 600;
    }
  }
}

.list-section {
  background: #fff;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
}

.release-list {
  :deep(.van-pull-refresh__head) {
    margin-bottom: 10px;
  }
}

.record-item {
  margin-bottom: 16px;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  
  :deep(.van-cell) {
    padding: 0;
    border: none;
  }
}

.record-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: #fafafa;
  border-bottom: 1px solid #f0f0f0;
}

.record-title {
  display: flex;
  align-items: center;
  gap: 12px;
}

.package-type {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 12px;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
}

.version-number {
  font-size: 18px;
  font-weight: 600;
  color: #2c3e50;
}

.current-tag {
  font-size: 12px;
}

.record-content {
  padding: 20px;
}

.record-info {
  margin-bottom: 16px;
}

.info-item {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  font-size: 14px;
  
  .label {
    color: #666;
    margin-right: 8px;
    min-width: 80px;
  }
  
  .value {
    color: #2c3e50;
  }
}

.record-desc {
  
  .label {
    display: block;
    font-weight: 600;
    color: #2c3e50;
    margin-bottom: 8px;
    font-size: 14px;
  }
  
  .desc-content {
    background: #f5f7fa;
    padding: 12px;
    border-radius: 8px;
    color: #333;
    font-size: 14px;
    line-height: 1.6;
    white-space: pre-wrap;
  }
}

.record-actions {
  display: flex;
  justify-content: flex-end;
  padding: 0 20px 16px;
}

@media screen and (max-width: 768px) {
  .package-list {
    padding: 10px;
  }
  
  .page-title {
    font-size: 20px;
  }
  
  .filter-section {
    padding: 16px;
  }
  
  .type-radio-group {
    flex-direction: column;
    gap: 8px;
    
    .type-radio {
      margin-bottom: 8px;
    }
  }
  
  .record-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
  
  .info-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    
    .label {
      min-width: auto;
    }
  }
}
</style>